import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { computePosition, flip, shift } from "@floating-ui/dom";
import type { Editor } from "@tiptap/react";
import { posToDOMRect, ReactRenderer } from "@tiptap/react";

import TagMentionList, { type TagMentionListHandle } from "../TagMentionList";
import { TagMentionAttrs, TagSuggestionItem } from "../types";

import { useTagSuggestions } from "./useTagSuggestions";

interface TagSuggestionProps {
  query: string;
  editor: Editor;
  clientRect?: (() => DOMRect | null) | DOMRect | null;
  event?: KeyboardEvent;
  range: { from: number; to: number };
}

interface UseTagMentionSuggestionOptions {
  allowEntityTypeTags?: boolean;
  allowUserTags?: boolean;
}

interface SuggestionKeyDownProps {
  event: KeyboardEvent;
}

interface SuggestionState {
  element: HTMLElement;
  component: ReactRenderer<TagMentionListHandle>;
}

const updatePosition = (editor: Editor, element: HTMLElement): void => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.zIndex = "9999";
  });
};

export const useTagMentionSuggestion = (options?: UseTagMentionSuggestionOptions) => {
  const { allowEntityTypeTags = false, allowUserTags = true } = options || {};

  // State for query - triggers re-renders and re-fetches
  const [query, setQuery] = useState("");

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);

  const { items, isLoading } = useTagSuggestions({
    query,
    enabled: true,
    allowEntityTypeTags,
    allowUserTags,
    currentFolderId,
  });

  // Refs for values needed in TipTap callbacks (to avoid stale closures)
  const itemsRef = useRef<TagSuggestionItem[]>(items);
  const isLoadingRef = useRef(isLoading);
  const currentFolderIdRef = useRef<number | null>(currentFolderId);
  const stateRef = useRef<SuggestionState | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const rangeRef = useRef<{ from: number; to: number }>({ from: 0, to: 0 });

  // Keep refs in sync with current values
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
  }, [currentFolderId]);

  // Create command function using refs for current values
  const createCommand = useCallback(
    (): ((attrs: TagMentionAttrs) => void) => (attrs: TagMentionAttrs) => {
      const editor = editorRef.current;
      const range = rangeRef.current;
      if (!editor) {
        return;
      }

      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent([
          {
            type: "tagMention",
            attrs,
          },
          {
            type: "text",
            text: " ",
          },
        ])
        .run();
    },
    [],
  );

  // Update component and reposition when items, isLoading, or currentFolderId changes
  useEffect(() => {
    const state = stateRef.current;
    const editor = editorRef.current;

    if (state?.component && editor) {
      // Update props with current values
      state.component.updateProps({
        items,
        isLoading,
        command: createCommand(),
        currentFolderId,
        onFolderSelect: setCurrentFolderId,
      });

      // Reposition after content change (size may have changed)
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (state.element && editorRef.current) {
          updatePosition(editorRef.current, state.element);
        }
      });
    }
  }, [items, isLoading, createCommand, currentFolderId]);

  // Create stable suggestion configuration
  const suggestion = useMemo(
    () => ({
      char: "@",
      items: ({ query: q }: { query: string }) => {
        setQuery(q);
        // Return empty - we handle items via the hook
        return [];
      },
      render: () => ({
        onStart: (props: TagSuggestionProps) => {
          setQuery(props.query);
          editorRef.current = props.editor;
          rangeRef.current = props.range;

          const component = new ReactRenderer(TagMentionList, {
            props: {
              items: itemsRef.current,
              isLoading: isLoadingRef.current,
              command: createCommand(),
              currentFolderId: currentFolderIdRef.current,
              onFolderSelect: setCurrentFolderId,
            },
            editor: props.editor,
          });

          const element = component.element as HTMLElement;
          element.style.position = "absolute";
          element.style.zIndex = "9999";
          element.setAttribute("data-tag-mention-menu", "true");

          document.body.appendChild(element);
          updatePosition(props.editor, element);

          stateRef.current = { element, component };
        },

        onUpdate: (props: TagSuggestionProps) => {
          setQuery(props.query);
          editorRef.current = props.editor;
          rangeRef.current = props.range;

          const state = stateRef.current;
          if (state) {
            state.component?.updateProps({
              items: itemsRef.current,
              isLoading: isLoadingRef.current,
              command: createCommand(),
              currentFolderId: currentFolderIdRef.current,
              onFolderSelect: setCurrentFolderId,
            });

            if (state.element) {
              updatePosition(props.editor, state.element);
            }
          }
        },

        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event?.key === "Escape") {
            return false;
          }

          const state = stateRef.current;
          if (state?.component?.ref && props.event) {
            return state.component.ref.onKeyDown({ event: props.event });
          }

          return false;
        },

        onExit: () => {
          const state = stateRef.current;
          if (state) {
            if (state.element?.parentNode) {
              state.element.parentNode.removeChild(state.element);
            }
            state.component?.destroy();
          }
          stateRef.current = null;
          editorRef.current = null;
          setQuery("");
          setCurrentFolderId(null);
        },
      }),
    }),
    [createCommand],
  );

  return suggestion;
};

export default useTagMentionSuggestion;
